import { useDebounce } from "@api";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import type { ReactNode } from "react";

const DEBOUNCE_MS = 750;
const DEFAULT_MIN_CHARS = 2;

export interface IAsyncAutocompleteProps<TOption> {
  label?: string;
  multiple?: boolean;
  value: TOption | TOption[] | null | undefined;
  onChange: (value: TOption | TOption[] | null) => void;
  onBlur?: () => void;
  name?: string;
  error?: boolean;
  helperText?: string;
  fetchOptions: (query: string) => Promise<TOption[]>;
  getOptionLabel: (option: TOption) => string;
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  minChars?: number;
  disabled?: boolean;
  startAdornment?: ReactNode;
  loading?: boolean;
}

export const AsyncAutocomplete = <TOption,>({
  label,
  multiple,
  value,
  onChange,
  onBlur,
  name,
  error,
  helperText,
  fetchOptions,
  getOptionLabel,
  isOptionEqualToValue,
  minChars = DEFAULT_MIN_CHARS,
  disabled,
  startAdornment,
  loading: externalLoading,
}: IAsyncAutocompleteProps<TOption>) => {
  const [options, setOptions] = useState<TOption[]>([]);
  const [internalLoading, setLoading] = useState(false);
  const loading = externalLoading || internalLoading;

  const runFetch = useDebounce(
    (query: string) => {
      setLoading(true);
      fetchOptions(query)
        .then(setOptions)
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    },
    DEBOUNCE_MS,
    [fetchOptions]
  );

  const handleInputChange = useCallback(
    (_event: unknown, inputValue: string, reason: string) => {
      if (reason !== "input" || inputValue.length < minChars) {
        setOptions([]);
        return;
      }
      runFetch(inputValue);
    },
    [runFetch, minChars]
  );

  return (
    <Autocomplete
      size="small"
      fullWidth
      multiple={multiple}
      disableCloseOnSelect={multiple}
      limitTags={1}
      options={options}
      loading={loading}
      disabled={disabled}
      value={value ?? (multiple ? [] : null)}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={handleInputChange}
      onBlur={onBlur}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(opts) => opts}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name={name}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: startAdornment ? (
                <>
                  {startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};
